// Core
import { ProfileData } from "@/domains/profile/types";
import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";

export type UtmParams = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  utm_id: string | null;
  utm_binom_id: string | null;
  ga_client_id: string | null;
  ga_session_id: string | null;
};

export type UserType = {
  hasHydrated: boolean;
  setHasHydrated: (v: boolean) => void;

  isAuth: boolean;
  token: string | null;
  refreshToken: string | null;
  user: ProfileData | null;

  setUser: (user: ProfileData) => void;
  getUser: () => ProfileData | null;

  getIsAuth: () => boolean;
  setIsAuth: (isAuth: boolean) => void;

  getToken: () => string | null;
  setToken: (token: string | null) => void;

  getRefreshToken: () => string | null;
  setRefreshToken: (token: string | null) => void;

  logout: () => void;
  login: (user: ProfileData) => void;

  setUserBalance: (balance: number) => void;
  isCrypto: () => boolean;

  utmParams: UtmParams | null;
  getUtmParams: () => UtmParams | null;
  setUtmParams: (params: UtmParams | null) => void;
};

const UserStore = create<UserType>()(
  devtools(
    persist<UserType>(
      (set, get) => ({
        hasHydrated: false,
        setHasHydrated: (v) => set({ hasHydrated: v }),

        isAuth: false,
        token: null,
        refreshToken: null,
        user: null,

        login: (user: ProfileData) => set({ isAuth: true, user }),
        logout: () => set({ isAuth: false, user: null, token: null, refreshToken: null }),

        getUser: () => get().user,
        getIsAuth: () => get().isAuth,

        getToken: () => get().token,
        setToken: (token: string | null) => set({ token }),

        getRefreshToken: () => get().refreshToken,
        setRefreshToken: (refreshToken: string | null) => set({ refreshToken }),

        setUser: (user: ProfileData) => set({ isAuth: true, user }),
        setIsAuth: (isAuth: boolean) => set({ isAuth }),

        setUserBalance: (balance: number) => {
          const user = get().user;
          if (!user) return;

          set({
            user: {
              ...user,
              balance: {
                ...user.balance,
                amount: Number(balance),
                currency: user.balance.currency || "USD",
                bonus: user.balance.bonus || 0,
              },
            },
          });
        },

        isCrypto: () => get().user?.balance.currency === "USD",

        utmParams: null,
        getUtmParams: () => get().utmParams,
        setUtmParams: (utmParams: UtmParams | null) => set({ utmParams }),
      }),
      {
        name: "user-storage",
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: () => (state, error) => {
          if (!error) state?.setHasHydrated(true);
        },
      },
    ),
  ),
);

export default UserStore;
